import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  Button,
  Alert,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Chip,
  Input,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  ModalClose,
  Textarea,
  IconButton,
  Tooltip,
} from '@mui/joy';
import {
  Users as GroupOutlined,
  CurrencyDollar as AttachMoneyOutlined,
  ClockClockwise as PendingActionsOutlined,
  TrendUp as TrendingUpOutlined,
  CheckCircle as CheckCircleOutlined,
  XCircle as CancelOutlined,
  PencilSimple as EditOutlined,
  Eye as VisibilityOutlined,
} from '@phosphor-icons/react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import { ResponsiveTable } from '../../components/common/ResponsiveTable';
import { AdminQueryError } from '../../components/admin/AdminQueryError';
import { getApiErrorMessage } from '../../lib/apiError';
import type { PaginatedResult } from '../../types/admin';
import { format } from 'date-fns';

interface AffiliateStats {
  total_affiliates: number;
  active_affiliates: number;
  pending_applications: number;
  total_referrals: number;
  successful_referrals: number;
  total_commissions_paid: number;
  pending_commissions: number;
  monthly_breakdown: Record<
    string,
    {
      commissions: number;
      referrals: number;
    }
  >;
}

interface AffiliateApplication {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  business_type: string;
  status: string;
  expected_referrals_per_month: number;
  created_at: string;
  business_name?: string;
  marketing_experience?: string;
  first_name: string;
  last_name: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  business_ein?: string;
  tax_id?: string;
  bank_account_holder_name?: string;
  bank_routing_number?: string;
  bank_account_number?: string;
  bank_account_type?: string;
  how_did_you_hear?: string;
  w9_document?: string;
  additional_tax_documents?: string;
  terms_accepted_at?: string;
  terms_ip_address?: string;
  reviewed_at?: string;
  reviewed_by?: string;
  review_notes?: string;
}

interface AffiliateProfile {
  id: string;
  user_name: string;
  user_email: string;
  referral_code: string;
  commission_rate: number;
  status: string;
  total_referrals: number;
  successful_referrals: number;
  total_commissions_earned: number;
  total_commissions_paid: number;
  pending_commissions: number;
  created_at: string;
  last_activity?: string;
}

export const AdminAffiliateDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedApplication, setSelectedApplication] = useState<AffiliateApplication | null>(null);
  const [selectedAffiliate, setSelectedAffiliate] = useState<AffiliateProfile | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [reviewNotes, setReviewNotes] = useState('');
  const [newCommissionRate, setNewCommissionRate] = useState<string>('');

  const queryClient = useQueryClient();

  // Fetch affiliate statistics
  const {
    data: stats,
        error: statsError,
    refetch: retryStats,
  } = useQuery<AffiliateStats>({
    queryKey: ['affiliate-stats'],
    queryFn: async (): Promise<AffiliateStats> => {
      const res = await apiClient.get('/api/affiliates/stats/');
      return res.data as AffiliateStats;
    },
  });

  const loadPage = async <T,>(path: string, page: number): Promise<PaginatedResult<T>> => {
    const { data } = await apiClient.get<T[] | PaginatedResult<T>>(`${path}?page=${page}&page_size=25`);
    return Array.isArray(data) ? { results: data, count: data.length, next: null, previous: null } : data;
  };
  const applicationsQuery = useInfiniteQuery({
    queryKey: ['affiliate-applications'],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      loadPage<AffiliateApplication>('/api/affiliates/applications/pending/', pageParam),
    getNextPageParam: (last, pages) => (last.next ? pages.length + 1 : undefined),
  });
  const affiliatesQuery = useInfiniteQuery({
    queryKey: ['affiliate-profiles'],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => loadPage<AffiliateProfile>('/api/affiliates/profiles/', pageParam),
    getNextPageParam: (last, pages) => (last.next ? pages.length + 1 : undefined),
  });
  const applications = applicationsQuery.data?.pages.flatMap((page) => page.results) ?? [];
  const affiliates = affiliatesQuery.data?.pages.flatMap((page) => page.results) ?? [];
  const applicationsLoading = applicationsQuery.isPending;
  const affiliatesLoading = affiliatesQuery.isPending;

  // Approve/Reject application mutation
  const reviewApplicationMutation = useMutation({
    mutationFn: async ({
      id,
      action,
      notes,
    }: {
      id: string;
      action: 'approve' | 'reject';
      notes: string;
    }) => {
      if (action === 'approve') {
        return apiClient.post(`/api/affiliates/applications/${id}/approve/`, {});
      } else {
        return apiClient.post(`/api/affiliates/applications/${id}/reject/`, {
          rejection_reason: notes,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliate-applications'] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-profiles'] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-stats'] });
      setReviewModalOpen(false);
      setSelectedApplication(null);
      setReviewNotes('');
    },
  });

  // Update commission rate mutation
  const updateCommissionMutation = useMutation({
    mutationFn: async ({ id, rate }: { id: string; rate: string }) => {
      return apiClient.post(`/api/affiliates/profiles/${id}/update_commission_rate/`, {
        commission_rate: rate,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliate-profiles'] });
      setEditModalOpen(false);
      setSelectedAffiliate(null);
      setNewCommissionRate('');
    },
  });

  // Suspend/Reactivate affiliate mutation
  const toggleAffiliateMutation = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'suspend' | 'reactivate' }) => {
      return apiClient.post(`/api/affiliates/profiles/${id}/${action}/`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliate-profiles'] });
    },
  });

  const handleReviewApplication = (application: AffiliateApplication, action: 'approve' | 'reject') => {
    reviewApplicationMutation.reset();
    setSelectedApplication(application);
    setReviewAction(action);
    setReviewModalOpen(true);
  };

  const handleEditAffiliate = (affiliate: AffiliateProfile) => {
    updateCommissionMutation.reset();
    setSelectedAffiliate(affiliate);
    setNewCommissionRate(affiliate.commission_rate.toString());
    setEditModalOpen(true);
  };

  const submitReview = () => {
    if (selectedApplication) {
      reviewApplicationMutation.mutate({
        id: selectedApplication.id,
        action: reviewAction,
        notes: reviewNotes,
      });
    }
  };

  const submitCommissionUpdate = () => {
    if (selectedAffiliate && newCommissionRate) {
      updateCommissionMutation.mutate({
        id: selectedAffiliate.id,
        rate: newCommissionRate,
      });
    }
  };

  return (
    <Box sx={{ p: { xs: 0, md: 2 } }}>
      <Stack spacing={3}>
        <AdminQueryError
          error={statsError}
          retry={retryStats}
          message="Affiliate totals could not be loaded."
        />
        <AdminQueryError
          error={applicationsQuery.error}
          retry={() =>
            applicationsQuery.isFetchNextPageError
              ? applicationsQuery.fetchNextPage()
              : applicationsQuery.refetch()
          }
          message="Applications could not be loaded."
        />
        <AdminQueryError
          error={affiliatesQuery.error}
          retry={() =>
            affiliatesQuery.isFetchNextPageError ? affiliatesQuery.fetchNextPage() : affiliatesQuery.refetch()
          }
          message="Affiliates could not be loaded."
        />
        {toggleAffiliateMutation.error && (
          <Alert color="danger">
            {getApiErrorMessage(toggleAffiliateMutation.error, 'The affiliate status could not be changed.')}
          </Alert>
        )}
        {/* Header */}
        <Box>
          <Typography level="h2" sx={{ mb: 1 }}>
            Affiliate Management
          </Typography>
          <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
            Manage affiliate applications, profiles, and system performance
          </Typography>
        </Box>

        {/* System Statistics */}
        <Grid container spacing={3}>
          <Grid xs={12} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'primary.100' }}>
                    <GroupOutlined color="var(--joy-palette-primary-500)" />
                  </Box>
                  <Box>
                    <Typography level="title-lg">{stats?.total_affiliates || 0}</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Total Affiliates
                    </Typography>
                  </Box>
                </Stack>
                <Typography level="body-xs" color="success" sx={{ mt: 1 }}>
                  {stats?.active_affiliates || 0} active
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'warning.100' }}>
                    <PendingActionsOutlined color="var(--joy-palette-warning-500)" />
                  </Box>
                  <Box>
                    <Typography level="title-lg">{stats?.pending_applications || 0}</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Pending Applications
                    </Typography>
                  </Box>
                </Stack>
                <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                  Awaiting review
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'success.100' }}>
                    <TrendingUpOutlined color="var(--joy-palette-success-500)" />
                  </Box>
                  <Box>
                    <Typography level="title-lg">{stats?.total_referrals || 0}</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Total Referrals
                    </Typography>
                  </Box>
                </Stack>
                <Typography level="body-xs" color="success" sx={{ mt: 1 }}>
                  {stats?.successful_referrals || 0} converted
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'info.100' }}>
                    <AttachMoneyOutlined color="var(--joy-palette-info-500)" />
                  </Box>
                  <Box>
                    <Typography level="title-lg">
                      ${stats?.total_commissions_paid?.toLocaleString() || '0'}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Total Paid Out
                    </Typography>
                  </Box>
                </Stack>
                <Typography level="body-xs" color="warning" sx={{ mt: 1 }}>
                  ${stats?.pending_commissions?.toLocaleString() || '0'} pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Management Tabs */}
        <Card variant="outlined">
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)}>
            <TabList>
              <Tab>Pending Applications ({applications?.length || 0})</Tab>
              <Tab>Affiliates ({affiliates?.length || 0})</Tab>
              <Tab>System Analytics</Tab>
            </TabList>

            {/* Pending Applications Tab */}
            <TabPanel value={0}>
              <Stack spacing={3}>
                <Typography level="title-md">Pending Applications</Typography>

                {applicationsLoading ? (
                  <Typography>Loading applications...</Typography>
                ) : applications?.length === 0 ? (
                  <Alert color="neutral">No pending applications at this time.</Alert>
                ) : (
                  <ResponsiveTable
                    rows={applications}
                    getRowKey={(row) => row.id}
                    columns={[
                      {
                        key: 'name',
                        header: 'Name',
                        mobileVariant: 'header',
                        render: (row) => (
                          <Box>
                            <Typography level="title-sm">{row.full_name}</Typography>
                            <Typography level="body-xs">{row.business_name}</Typography>
                          </Box>
                        ),
                      },
                      {
                        key: 'email',
                        header: 'Email',
                        render: (row) => (
                          <Typography sx={{ overflowWrap: 'anywhere' }}>{row.email}</Typography>
                        ),
                      },
                      {
                        key: 'type',
                        header: 'Business',
                        render: (row) => row.business_type.replace(/_/g, ' '),
                      },
                      {
                        key: 'referrals',
                        header: 'Expected referrals',
                        render: (row) => `${row.expected_referrals_per_month}/month`,
                      },
                      {
                        key: 'applied',
                        header: 'Applied',
                        render: (row) => format(new Date(row.created_at), 'MMM d, yyyy'),
                      },
                    ]}
                    rowActions={(application) => (
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Approve Application">
                          <IconButton
                            aria-label="Approve Application"
                            size="sm"
                            color="success"
                            onClick={() => handleReviewApplication(application, 'approve')}
                          >
                            <CheckCircleOutlined />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject Application">
                          <IconButton
                            aria-label="Reject Application"
                            size="sm"
                            color="danger"
                            onClick={() => handleReviewApplication(application, 'reject')}
                          >
                            <CancelOutlined />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Details">
                          <IconButton
                            aria-label="View Details"
                            size="sm"
                            color="neutral"
                            onClick={() => {
                              setSelectedApplication(application);
                              setDetailsModalOpen(true);
                            }}
                          >
                            <VisibilityOutlined />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  />
                )}
                {(activeTab === 0 ? applicationsQuery : affiliatesQuery).hasNextPage && (
                  <Button
                    variant="outlined"
                    loading={(activeTab === 0 ? applicationsQuery : affiliatesQuery).isFetchingNextPage}
                    onClick={() => (activeTab === 0 ? applicationsQuery : affiliatesQuery).fetchNextPage()}
                  >
                    Load more
                  </Button>
                )}
              </Stack>
            </TabPanel>

            {/* Affiliates Tab */}
            <TabPanel value={1}>
              <Stack spacing={3}>
                <Typography level="title-md">Affiliates</Typography>

                {affiliatesLoading ? (
                  <Typography>Loading affiliates...</Typography>
                ) : (
                  <ResponsiveTable
                    rows={affiliates}
                    getRowKey={(row) => row.id}
                    columns={[
                      {
                        key: 'name',
                        header: 'Name',
                        mobileVariant: 'header',
                        render: (row) => row.user_name,
                      },
                      {
                        key: 'email',
                        header: 'Email',
                        render: (row) => (
                          <Typography sx={{ overflowWrap: 'anywhere' }}>{row.user_email}</Typography>
                        ),
                      },
                      {
                        key: 'rate',
                        header: 'Commission',
                        render: (row) => `${row.commission_rate}%`,
                      },
                      {
                        key: 'referrals',
                        header: 'Referrals',
                        render: (row) => `${row.total_referrals} total · ${row.successful_referrals} active`,
                      },
                      {
                        key: 'earned',
                        header: 'Earnings',
                        render: (row) =>
                          `$${Number(row.total_commissions_earned).toLocaleString()} · $${Number(row.pending_commissions).toLocaleString()} pending`,
                      },
                      {
                        key: 'status',
                        header: 'Status',
                        render: (row) => (
                          <Chip size="sm" color={row.status === 'active' ? 'success' : 'warning'}>
                            {row.status}
                          </Chip>
                        ),
                      },
                    ]}
                    emptyState={<Typography sx={{ p: 2 }}>No affiliates in this view.</Typography>}
                    rowActions={(affiliate) => (
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit Commission Rate">
                          <IconButton
                            aria-label="Edit Commission Rate"
                            size="sm"
                            color="primary"
                            onClick={() => handleEditAffiliate(affiliate)}
                          >
                            <EditOutlined />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={affiliate.status === 'active' ? 'Suspend' : 'Reactivate'}>
                          <IconButton
                            aria-label={
                              affiliate.status === 'active' ? 'Suspend affiliate' : 'Reactivate affiliate'
                            }
                            disabled={toggleAffiliateMutation.isPending}
                            size="sm"
                            color={affiliate.status === 'active' ? 'warning' : 'success'}
                            onClick={() =>
                              toggleAffiliateMutation.mutate({
                                id: affiliate.id,
                                action: affiliate.status === 'active' ? 'suspend' : 'reactivate',
                              })
                            }
                          >
                            {affiliate.status === 'active' ? <CancelOutlined /> : <CheckCircleOutlined />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  />
                )}
                {(activeTab === 0 ? applicationsQuery : affiliatesQuery).hasNextPage && (
                  <Button
                    variant="outlined"
                    loading={(activeTab === 0 ? applicationsQuery : affiliatesQuery).isFetchingNextPage}
                    onClick={() => (activeTab === 0 ? applicationsQuery : affiliatesQuery).fetchNextPage()}
                  >
                    Load more
                  </Button>
                )}
              </Stack>
            </TabPanel>

            {/* Analytics Tab */}
            <TabPanel value={2}>
              <Stack spacing={3}>
                <Typography level="title-md">System Analytics</Typography>

                <Card variant="outlined">
                  <CardContent>
                    <Typography level="title-sm" sx={{ mb: 2 }}>
                      Monthly Performance Breakdown
                    </Typography>
                    <Grid container spacing={2}>
                      {stats?.monthly_breakdown &&
                        Object.entries(stats.monthly_breakdown).map(([month, data]) => (
                          <Grid key={month} xs={12} sm={6} md={4}>
                            <Box
                              sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 'md',
                              }}
                            >
                              <Typography level="body-sm" fontWeight="lg">
                                {month}
                              </Typography>
                              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                {data.referrals} referrals
                              </Typography>
                              <Typography level="body-xs" color="success">
                                ${data.commissions.toLocaleString()} commissions
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                    </Grid>
                  </CardContent>
                </Card>

                <Grid container spacing={3}>
                  <Grid xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography level="title-sm" sx={{ mb: 2 }}>
                          Conversion Rate
                        </Typography>
                        <Typography level="h3" color="primary">
                          {stats?.total_referrals
                            ? Math.round((stats.successful_referrals / stats.total_referrals) * 100)
                            : 0}
                          %
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                          {stats?.successful_referrals} of {stats?.total_referrals} referrals converted
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography level="title-sm" sx={{ mb: 2 }}>
                          Average Commission per Referral
                        </Typography>
                        <Typography level="h3" color="success">
                          $
                          {stats?.total_referrals
                            ? Math.round((stats.total_commissions_paid || 0) / stats.total_referrals)
                            : 0}
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                          Based on successful conversions
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Stack>
            </TabPanel>
          </Tabs>
        </Card>

        {/* Review Application Modal */}
        <Modal open={reviewModalOpen} onClose={() => setReviewModalOpen(false)}>
          <ModalDialog
            sx={{
              width: 440,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: '90dvh',
              overflow: 'auto',
            }}
          >
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              {reviewAction === 'approve' ? 'Approve' : 'Reject'} Application
            </Typography>

            {reviewApplicationMutation.error && (
              <Alert color="danger">
                {getApiErrorMessage(
                  reviewApplicationMutation.error,
                  'The application could not be reviewed.',
                )}
              </Alert>
            )}
            {selectedApplication && (
              <>
                <Typography level="body-md" sx={{ mb: 2 }}>
                  <strong>{selectedApplication.full_name}</strong> ({selectedApplication.email})
                </Typography>

                {reviewAction === 'reject' && (
                  <FormControl sx={{ mb: 3 }}>
                    <FormLabel>
                      Rejection Reason
                    </FormLabel>
                    <Textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Please provide a reason for rejection..."
                      minRows={3}
                    />
                  </FormControl>
                )}

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="solid"
                    color={reviewAction === 'approve' ? 'success' : 'danger'}
                    onClick={submitReview}
                    loading={reviewApplicationMutation.isPending}
                  >
                    {reviewAction === 'approve' ? 'Approve' : 'Reject'}
                  </Button>
                  <Button variant="outlined" onClick={() => setReviewModalOpen(false)}>
                    Cancel
                  </Button>
                </Stack>
              </>
            )}
          </ModalDialog>
        </Modal>

        {/* Edit Affiliate Modal */}
        <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
          <ModalDialog
            sx={{
              width: 440,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: '90dvh',
              overflow: 'auto',
            }}
          >
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              Edit Commission Rate
            </Typography>

            {updateCommissionMutation.error && (
              <Alert color="danger">
                {getApiErrorMessage(
                  updateCommissionMutation.error,
                  'The commission rate could not be saved.',
                )}
              </Alert>
            )}
            {selectedAffiliate && (
              <>
                <Typography level="body-md" sx={{ mb: 2 }}>
                  <strong>{selectedAffiliate.user_name}</strong> ({selectedAffiliate.user_email})
                </Typography>

                <FormControl sx={{ mb: 3 }}>
                  <FormLabel>Commission Rate (%)</FormLabel>
                  <Input
                    type="number"
                    value={newCommissionRate}
                    onChange={(e) => setNewCommissionRate(e.target.value)}
                    placeholder="20"
                    endDecorator="%"
                    slotProps={{
                      input: {
                        min: 0,
                        max: 100,
                        step: 0.1,
                      },
                    }}
                  />
                </FormControl>

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="solid"
                    color="primary"
                    onClick={submitCommissionUpdate}
                    loading={updateCommissionMutation.isPending}
                    disabled={
                      !newCommissionRate.trim() ||
                      !Number.isFinite(Number(newCommissionRate)) ||
                      Number(newCommissionRate) < 0 ||
                      Number(newCommissionRate) > 100
                    }
                  >
                    Update Rate
                  </Button>
                  <Button variant="outlined" onClick={() => setEditModalOpen(false)}>
                    Cancel
                  </Button>
                </Stack>
              </>
            )}
          </ModalDialog>
        </Modal>

        {/* View Application Details Modal */}
        <Modal open={detailsModalOpen} onClose={() => setDetailsModalOpen(false)}>
          <ModalDialog
            sx={{
              width: 800,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: '90dvh',
              overflow: 'auto',
            }}
          >
            <ModalClose />
            <Typography level="h3" sx={{ mb: 2 }}>
              Application Details
            </Typography>

            {reviewApplicationMutation.error && (
              <Alert color="danger">
                {getApiErrorMessage(
                  reviewApplicationMutation.error,
                  'The application could not be reviewed.',
                )}
              </Alert>
            )}
            {selectedApplication && (
              <Stack spacing={3}>
                {/* Application Status */}
                <Alert
                  color={
                    selectedApplication.status === 'approved'
                      ? 'success'
                      : selectedApplication.status === 'rejected'
                        ? 'danger'
                        : 'warning'
                  }
                >
                  Status:{' '}
                  {selectedApplication.status.charAt(0).toUpperCase() + selectedApplication.status.slice(1)}
                </Alert>

                {/* Personal Information */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography level="title-lg" sx={{ mb: 2 }}>
                      Personal Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        <Typography level="body-sm" fontWeight="lg">
                          Name:
                        </Typography>
                        <Typography level="body-md">
                          {selectedApplication.first_name} {selectedApplication.last_name}
                        </Typography>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <Typography level="body-sm" fontWeight="lg">
                          Email:
                        </Typography>
                        <Typography level="body-md">{selectedApplication.email}</Typography>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <Typography level="body-sm" fontWeight="lg">
                          Phone:
                        </Typography>
                        <Typography level="body-md">{selectedApplication.phone}</Typography>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <Typography level="body-sm" fontWeight="lg">
                          Application Date:
                        </Typography>
                        <Typography level="body-md">
                          {format(new Date(selectedApplication.created_at), 'PPp')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Address Information */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography level="title-lg" sx={{ mb: 2 }}>
                      Address
                    </Typography>
                    <Typography level="body-md">
                      {selectedApplication.address_line_1}
                      <br />
                      {selectedApplication.address_line_2 && (
                        <>
                          {selectedApplication.address_line_2}
                          <br />
                        </>
                      )}
                      {selectedApplication.city}, {selectedApplication.state} {selectedApplication.zip_code}
                      <br />
                      {selectedApplication.country}
                    </Typography>
                  </CardContent>
                </Card>

                {/* Business Information */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography level="title-lg" sx={{ mb: 2 }}>
                      Business Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        <Typography level="body-sm" fontWeight="lg">
                          Business Type:
                        </Typography>
                        <Chip variant="soft" size="sm">
                          {selectedApplication.business_type.replace('_', ' ')}
                        </Chip>
                      </Grid>
                      {selectedApplication.business_name && (
                        <Grid xs={12} md={6}>
                          <Typography level="body-sm" fontWeight="lg">
                            Business Name:
                          </Typography>
                          <Typography level="body-md">{selectedApplication.business_name}</Typography>
                        </Grid>
                      )}
                      {selectedApplication.business_ein && (
                        <Grid xs={12} md={6}>
                          <Typography level="body-sm" fontWeight="lg">
                            EIN:
                          </Typography>
                          <Typography level="body-md">{selectedApplication.business_ein}</Typography>
                        </Grid>
                      )}
                      <Grid xs={12} md={6}>
                        <Typography level="body-sm" fontWeight="lg">
                          Expected Referrals/Month:
                        </Typography>
                        <Typography level="body-md">
                          {selectedApplication.expected_referrals_per_month}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Banking Information */}
                {selectedApplication.bank_account_holder_name && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-lg" sx={{ mb: 2 }}>
                        Banking Information (ACH)
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid xs={12} md={6}>
                          <Typography level="body-sm" fontWeight="lg">
                            Account Holder:
                          </Typography>
                          <Typography level="body-md">
                            {selectedApplication.bank_account_holder_name}
                          </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                          <Typography level="body-sm" fontWeight="lg">
                            Account Type:
                          </Typography>
                          <Typography level="body-md">
                            {selectedApplication.bank_account_type
                              ? selectedApplication.bank_account_type.charAt(0).toUpperCase() +
                                selectedApplication.bank_account_type.slice(1)
                              : 'Not specified'}
                          </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                          <Typography level="body-sm" fontWeight="lg">
                            Routing Number:
                          </Typography>
                          <Typography level="body-md" sx={{ fontFamily: 'monospace' }}>
                            ****
                            {selectedApplication.bank_routing_number?.slice(-4) || '****'}
                          </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                          <Typography level="body-sm" fontWeight="lg">
                            Account Number:
                          </Typography>
                          <Typography level="body-md" sx={{ fontFamily: 'monospace' }}>
                            ****
                            {selectedApplication.bank_account_number?.slice(-4) || '****'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Marketing Information */}
                {(selectedApplication.marketing_experience || selectedApplication.how_did_you_hear) && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-lg" sx={{ mb: 2 }}>
                        Marketing Information
                      </Typography>
                      {selectedApplication.how_did_you_hear && (
                        <Box sx={{ mb: 2 }}>
                          <Typography level="body-sm" fontWeight="lg">
                            How They Found Us:
                          </Typography>
                          <Typography level="body-md">{selectedApplication.how_did_you_hear}</Typography>
                        </Box>
                      )}
                      {selectedApplication.marketing_experience && (
                        <Box>
                          <Typography level="body-sm" fontWeight="lg">
                            Marketing Experience:
                          </Typography>
                          <Typography level="body-md">{selectedApplication.marketing_experience}</Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Tax Documents */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography level="title-lg" sx={{ mb: 2 }}>
                      Tax Documents
                    </Typography>
                    <Stack spacing={1}>
                      <Box>
                        <Typography level="body-sm" fontWeight="lg">
                          W-9 Document:
                        </Typography>
                        {selectedApplication.w9_document ? (
                          <Chip color="success" variant="soft" size="sm">
                            Uploaded
                          </Chip>
                        ) : (
                          <Chip color="neutral" variant="soft" size="sm">
                            Not Uploaded
                          </Chip>
                        )}
                      </Box>
                      {selectedApplication.tax_id && (
                        <Box>
                          <Typography level="body-sm" fontWeight="lg">
                            Tax ID:
                          </Typography>
                          <Typography level="body-md" sx={{ fontFamily: 'monospace' }}>
                            ***-**-{selectedApplication.tax_id.slice(-4)}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

                {/* Terms Acceptance */}
                {selectedApplication.terms_accepted_at && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-lg" sx={{ mb: 2 }}>
                        Terms & Conditions
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid xs={12} md={6}>
                          <Typography level="body-sm" fontWeight="lg">
                            Accepted At:
                          </Typography>
                          <Typography level="body-md">
                            {format(new Date(selectedApplication.terms_accepted_at), 'PPp')}
                          </Typography>
                        </Grid>
                        {selectedApplication.terms_ip_address && (
                          <Grid xs={12} md={6}>
                            <Typography level="body-sm" fontWeight="lg">
                              IP Address:
                            </Typography>
                            <Typography level="body-md" sx={{ fontFamily: 'monospace' }}>
                              {selectedApplication.terms_ip_address}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Review Information (if reviewed) */}
                {selectedApplication.reviewed_at && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-lg" sx={{ mb: 2 }}>
                        Review Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid xs={12} md={6}>
                          <Typography level="body-sm" fontWeight="lg">
                            Reviewed At:
                          </Typography>
                          <Typography level="body-md">
                            {format(new Date(selectedApplication.reviewed_at), 'PPp')}
                          </Typography>
                        </Grid>
                        {selectedApplication.reviewed_by && (
                          <Grid xs={12} md={6}>
                            <Typography level="body-sm" fontWeight="lg">
                              Reviewed By:
                            </Typography>
                            <Typography level="body-md">{selectedApplication.reviewed_by}</Typography>
                          </Grid>
                        )}
                        {selectedApplication.review_notes && (
                          <Grid xs={12}>
                            <Typography level="body-sm" fontWeight="lg">
                              Review Notes:
                            </Typography>
                            <Typography level="body-md">{selectedApplication.review_notes}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Action Buttons */}
                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  {selectedApplication.status === 'pending' && (
                    <>
                      <Button
                        variant="solid"
                        color="success"
                        onClick={() => {
                          setDetailsModalOpen(false);
                          handleReviewApplication(selectedApplication, 'approve');
                        }}
                      >
                        Approve Application
                      </Button>
                      <Button
                        variant="solid"
                        color="danger"
                        onClick={() => {
                          setDetailsModalOpen(false);
                          handleReviewApplication(selectedApplication, 'reject');
                        }}
                      >
                        Reject Application
                      </Button>
                    </>
                  )}
                  <Button variant="outlined" onClick={() => setDetailsModalOpen(false)}>
                    Close
                  </Button>
                </Stack>
              </Stack>
            )}
          </ModalDialog>
        </Modal>
      </Stack>
    </Box>
  );
};
